import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { useState } from "react";
import PhoneInput from "../components/elements/PhoneInput";
import { AMERICAS_COUNTRIES } from "../config/config";
import { useCreateUser } from "./hooks/useCreateUser";

const GREEN = "#00C853";
const DARK = "#0D1420";

interface Props {
  open: boolean;
  onClose: () => void;
  refreshList?: () => void;
}

export function CreateUserDialog({ open, onClose, refreshList }: Props) {
  const [country, setCountry] = useState(
    AMERICAS_COUNTRIES.find((c) => c.code === "+57")
  );
  const [phone, setPhone] = useState("");
  const [fullName, setFullName] = useState("");
  const [documentType, setDocumentType] = useState("CC");
  const [document, setDocument] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [emailOptional, setEmailOptional] = useState("");
  const [address, setAddress] = useState("");
  const [isVerified, setIsVerified] = useState(false);
  const [isGuarantor, setIsGuarantor] = useState(false);

  const { createUser, isLoading, error } = useCreateUser();

  const handleSubmit = async () => {
    const success = await createUser({
      fullName,
      phone: `${country?.code}${phone}`,
      documentType,
      document,
      birthDate,
      emailOptional,
      address,
      isVerified,
      isGuarantor,
    });
    if (success) {
      refreshList?.();
      onClose();
      setPhone("");
      setFullName("");
      setDocument("");
      setBirthDate("");
      setEmailOptional("");
      setAddress("");
      setIsVerified(false);
      setIsGuarantor(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4, overflow: "hidden" } }}>
      <Box sx={{ bgcolor: DARK, px: 3, py: 2.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography sx={{ fontWeight: 800, fontSize: 18, color: "white" }}>Nuevo cliente</Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: "white", bgcolor: "#ffffff18" }}>
          <Close fontSize="small" />
        </IconButton>
      </Box>
      <DialogContent sx={{ p: 2, bgcolor: "#f7f9f7" }}>
        <Stack spacing={1.5}>
          <TextField fullWidth label="Nombre completo *" value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            sx={{ bgcolor: "white", "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />

          <PhoneInput phone={phone} setPhone={setPhone} country={country} setCountry={setCountry} />

          <Stack direction="row" spacing={1}>
            <TextField select label="Tipo doc. *" value={documentType}
              onChange={(e) => setDocumentType(e.target.value)}
              sx={{ width: 130, bgcolor: "white", "& .MuiOutlinedInput-root": { borderRadius: 2 } }}>
              {["CC", "CE", "TI", "PAS", "NIT"].map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
            <TextField fullWidth label="Número de documento *" value={document}
              onChange={(e) => setDocument(e.target.value)}
              sx={{ bgcolor: "white", "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
          </Stack>

          <TextField fullWidth label="Fecha de nacimiento *" type="date" value={birthDate}
            onChange={(e) => setBirthDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ bgcolor: "white", "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />

          <TextField fullWidth label="Email (opcional)" value={emailOptional}
            onChange={(e) => setEmailOptional(e.target.value)}
            sx={{ bgcolor: "white", "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />

          <TextField fullWidth label="Dirección (opcional)" value={address}
            onChange={(e) => setAddress(e.target.value)}
            sx={{ bgcolor: "white", "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />

          <Stack direction="row" spacing={1}>
            <Box sx={{ flex: 1, p: 1.5, borderRadius: 2, border: `2px solid ${isVerified ? GREEN : "#e0e0e0"}`, bgcolor: isVerified ? `${GREEN}11` : "white" }}>
              <FormControlLabel
                control={<Switch checked={isVerified} onChange={(e) => setIsVerified(e.target.checked)} sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: GREEN }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: GREEN } }} />}
                label={<Typography sx={{ fontSize: 13, fontWeight: 700, color: isVerified ? GREEN : "#667788" }}>Verificado</Typography>}
              />
            </Box>
            <Box sx={{ flex: 1, p: 1.5, borderRadius: 2, border: `2px solid ${isGuarantor ? GREEN : "#e0e0e0"}`, bgcolor: isGuarantor ? `${GREEN}11` : "white" }}>
              <FormControlLabel
                control={<Switch checked={isGuarantor} onChange={(e) => setIsGuarantor(e.target.checked)} sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: GREEN }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: GREEN } }} />}
                label={<Typography sx={{ fontSize: 13, fontWeight: 700, color: isGuarantor ? GREEN : "#667788" }}>Avalista</Typography>}
              />
            </Box>
          </Stack>
          {error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}
        </Stack>
      </DialogContent>
      <Box sx={{ px: 2.5, py: 2, display: "flex", gap: 1.5, bgcolor: "white", borderTop: "1px solid #f0f0f0" }}>
        <Button fullWidth onClick={onClose} disabled={isLoading}
          sx={{ borderRadius: "50px", textTransform: "none", fontWeight: 600, border: "1px solid #e0e0e0", color: "#667788" }}>
          Cancelar
        </Button>
        <Button fullWidth variant="contained" onClick={handleSubmit}
          disabled={!fullName || !phone || !document || !birthDate || isLoading}
          startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : null}
          sx={{ borderRadius: "50px", textTransform: "none", fontWeight: 700, bgcolor: GREEN, boxShadow: "none", "&:hover": { bgcolor: "#00b048" } }}>
          {isLoading ? "Creando..." : "Crear cliente"}
        </Button>
      </Box>
    </Dialog>
  );
}
